import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { tInterval } from "@/utils/translationUtils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Row, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import ActionButton from "../../../../../../../common/components/buttons/ActionButton";
import { rowGutter } from "../../../../../../../common/constants";
import { ConstructionMaterial, RoofType } from "../../../enums";
import { RealtyCalc } from "../../../types";

const colSpan = 5;

export const RealtyCalcHouseSection = () => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<RealtyCalc>();

  const roofType = Form.useWatch(["houseOrRecreationalBuildingData", "roofType"], form) as RoofType | undefined;
  const buildArea = Form.useWatch(["houseOrRecreationalBuildingData", "buildArea"], form) as number | undefined;

  return (
    <Card type="inner" className="card-box" title={t("calc.realty.sections.insuredObjectAndAreas")}>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "constructionMaterial"]}
            label={t("calc.realty.enums.constructionMaterial._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(ConstructionMaterial).map((material) => ({
                value: material,
                label: t("calc.realty.enums.constructionMaterial." + material),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "roofType"]}
            label={t("calc.realty.enums.roofType._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(RoofType).map((type) => ({
                value: type,
                label: t("calc.realty.enums.roofType." + type),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "buildArea"]}
            label={t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")}
            rules={[validations.notNull, validations.minNumber(1)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="divider-subheader">{t("calc.realty.sections.areas")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "cellarArea"]}
            label={t("calc.realty.attrs.houseOrRecreationalBuildingData.cellarArea")}
            rules={[
              validations.minNumber(0),
              validations.maxNumber(buildArea, t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")),
            ]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "residentialCellarArea"]}
            label={t("calc.realty.attrs.houseOrRecreationalBuildingData.residentialCellarArea")}
            rules={[
              validations.minNumber(0),
              validations.maxNumber(buildArea, t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")),
            ]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "atticArea"]}
            label={t("calc.realty.attrs.houseOrRecreationalBuildingData.atticArea")}
            rules={[
              validations.minNumber(0),
              validations.maxNumber(buildArea, t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")),
            ]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>

        {roofType === RoofType.PITCHED_WITH_ATTIC && (
          <Col span={colSpan}>
            <Form.Item
              name={["houseOrRecreationalBuildingData", "residentialAtticArea"]}
              label={t("calc.realty.attrs.houseOrRecreationalBuildingData.residentialAtticArea")}
              rules={[
                validations.minNumber(0),
                validations.maxNumber(buildArea, t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")),
              ]}
            >
              <InputNumber addon="area" />
            </Form.Item>
          </Col>
        )}

        <Col span={colSpan}>
          <Form.Item
            name={["houseOrRecreationalBuildingData", "garageArea"]}
            label={t("calc.realty.attrs.houseOrRecreationalBuildingData.garageArea")}
            rules={[validations.none, validations.minNumber(0)]}
          >
            <InputNumber addon="area" />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="divider-subheader">{t("calc.realty.sections.overgroundAreas")}</Divider>

      <Form.List name={["houseOrRecreationalBuildingData", "overgroundFloorAreas"]} initialValue={[null]}>
        {(fields, { add, remove }) => (
          <>
            <Row gutter={rowGutter}>
              {fields.map(({ key, ...field }) => (
                <Col span={colSpan} key={key}>
                  <Form.Item
                    {...field}
                    name={field.name}
                    label={tInterval(
                      "calc.realty.attrs.houseOrRecreationalBuildingData.overgroundFloorAreas_interval",
                      field.name + 1,
                    )}
                    rules={[
                      validations.notNull,
                      validations.minNumber(1),
                      validations.maxNumber(
                        buildArea,
                        t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea"),
                      ),
                    ]}
                  >
                    <InputNumber addon="area" />
                  </Form.Item>
                </Col>
              ))}
            </Row>

            <div className="margin-bottom-medium">
              <Space>
                <ActionButton icon="plus" disabled={fields.length >= 3} onClick={() => add()}>
                  {t("calc.realty.actions.overgroundAreaAdd")}
                </ActionButton>

                <ActionButton
                  icon="minus"
                  disabled={fields.length <= 1}
                  onClick={() => {
                    const name = fields[fields.length - 1]?.name;
                    if (name) {
                      return remove(name);
                    }
                  }}
                >
                  {t("calc.realty.actions.overgroundAreaDelete")}
                </ActionButton>
              </Space>
            </div>
          </>
        )}
      </Form.List>
    </Card>
  );
};
